import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { GET, POST, httpOk } from 'helpers/api'

function Index() {
	const { t } = useTranslation()

	const [apiUrl, setApiUrl]           = useState('')
	const [apiKey, setApiKey]           = useState('')
	const [isActive, setIsActive]       = useState(false)
	const [saving, setSaving]           = useState(false)
	const [testing, setTesting]         = useState(false)
	const [saveStatus, setSaveStatus]   = useState(null)
	const [testStatus, setTestStatus]   = useState(null)
	const [testMessage, setTestMessage] = useState('')

	useEffect(() => {
		loadSettings()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	async function loadSettings() {
		const res = await GET('/crm-settings', {}, { loader: true })
		if (httpOk(res)) {
			setApiUrl(res.data?.api_url || '')
			setApiKey(res.data?.api_key || '')
			setIsActive(!!res.data?.is_active)
		}
	}

	async function saveSettings(e) {
		e.preventDefault()
		setSaving(true)
		setSaveStatus(null)
		setIsActive(false)
		const res = await POST('/crm-settings', { api_url: apiUrl, api_key: apiKey }, { loader: false })
		setSaving(false)
		if (httpOk(res)) {
			setSaveStatus('ok')
			setTimeout(() => setSaveStatus(null), 3000)
		} else {
			setSaveStatus('err')
		}
	}

	async function testConnection(e) {
		e.preventDefault()
		setTesting(true)
		setTestStatus(null)
		setTestMessage('')
		const res = await POST('/crm-settings/test', {}, { loader: false })
		setTesting(false)
		if (httpOk(res)) {
			setTestStatus('ok')
			setIsActive(true)
			setTestMessage(t('crm_test_success'))
		} else {
			setTestStatus('err')
			setIsActive(false)
			setTestMessage(res?.data?.message || t('crm_test_failed'))
		}
	}

	const hasCredentials = apiUrl.trim() && apiKey.trim()

	return (
		<>
			<div className="page-header d-flex justify-content-between align-items-center">
				<h4 className="vertical-center mb-0">
					{t('crm_integration')}
				</h4>
				{isActive && (
					<span className="badge bg-success fs-6 px-3 py-2">
						<i className="uil uil-check-circle me-1"></i>
						{t('crm_connected')}
					</span>
				)}
				{!isActive && hasCredentials && (
					<span className="badge bg-warning text-dark fs-6 px-3 py-2">
						<i className="uil uil-exclamation-triangle me-1"></i>
						{t('crm_not_tested')}
					</span>
				)}
			</div>

			<div className="row">
				<div className="col-md-7 col-lg-6">

					{/* API credentials */}
					<div className="card mb-3">
						<div className="card-body">
							<h6 className="fw-bold mb-3">
								<i className="uil uil-key-skeleton-alt me-2 text-primary"></i>
								{t('crm_credentials')}
							</h6>

							<form onSubmit={saveSettings} autoComplete="off">
								<div className="form-group mb-3">
									<label className="form-label">{t('crm_api_url')}</label>
									<input
										type="url"
										className="form-control font-monospace"
										placeholder="https://crm.example.com/api"
										value={apiUrl}
										onChange={e => setApiUrl(e.target.value)}
										style={{ fontSize: '13px' }}
									/>
									<div className="form-text">{t('crm_url_hint')}</div>
								</div>

								<div className="form-group mb-3">
									<label className="form-label">{t('crm_api_key')}</label>
									<input
										type="password"
										className="form-control font-monospace"
										placeholder="••••••••••••••••••••"
										value={apiKey}
										onChange={e => {
											setApiKey(e.target.value)
											setIsActive(false)
										}}
										style={{ fontSize: '13px', letterSpacing: '2px' }}
									/>
									<div className="form-text">{t('crm_key_hint')}</div>
								</div>

								{saveStatus === 'ok' && (
									<div className="alert alert-success py-2 mb-2" style={{ fontSize: '13px' }}>
										<i className="uil uil-check me-1"></i> {t('saved')}
									</div>
								)}
								{saveStatus === 'err' && (
									<div className="alert alert-danger py-2 mb-2" style={{ fontSize: '13px' }}>
										<i className="uil uil-exclamation-triangle me-1"></i> {t('crm_save_error')}
									</div>
								)}

								<button
									type="submit"
									className="btn btn-primary"
									disabled={saving || !hasCredentials}>
									{saving
										? <><span className="spinner-border spinner-border-sm me-2"></span>{t('saving')}</>
										: <><i className="uil uil-save me-1"></i>{t('save')}</>}
								</button>
							</form>
						</div>
					</div>

					{/* Test connection */}
					<div className="card mb-3">
						<div className="card-body">
							<h6 className="fw-bold mb-3">
								<i className="uil uil-wifi me-2 text-primary"></i>
								{t('crm_test_title')}
							</h6>

							<p className="text-muted mb-3" style={{ fontSize: '13px' }}>
								{t('crm_test_desc')}
							</p>

							{testStatus === 'ok' && (
								<div className="alert alert-success py-2 mb-3" style={{ fontSize: '13px' }}>
									<i className="uil uil-check-circle me-1"></i> {testMessage}
								</div>
							)}
							{testStatus === 'err' && (
								<div className="alert alert-danger py-2 mb-3" style={{ fontSize: '13px' }}>
									<i className="uil uil-exclamation-triangle me-1"></i> {testMessage}
								</div>
							)}

							<button
								className="btn btn-outline-primary"
								onClick={testConnection}
								disabled={testing || !hasCredentials}>
								{testing
									? <><span className="spinner-border spinner-border-sm me-2"></span>{t('crm_testing')}</>
									: <><i className="uil uil-plug me-1"></i>{t('crm_test_btn')}</>}
							</button>
						</div>
					</div>

					{/* Info */}
					<div className="card border-0" style={{ background: '#f8f9fa' }}>
						<div className="card-body py-3">
							<p className="mb-0 text-muted" style={{ fontSize: '13px' }}>
								<i className="uil uil-info-circle me-1 text-primary"></i>
								{t('crm_info')}
							</p>
						</div>
					</div>

				</div>
			</div>
		</>
	)
}

export default Index
